// src/pages/Accounting/Reports/AccountList/components/AskVelaPanel.tsx
// Right-side Vela chat panel. Suggested prompts + free input.

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { VELA_SUGGESTED_PROMPTS } from '../constants';

interface AskVelaPanelProps {
  open: boolean;
  onClose: () => void;
  onAsk: (prompt: string) => void;
}

export default function AskVelaPanel({ open, onClose, onAsk }: AskVelaPanelProps) {
  const [input, setInput] = useState('');

  if (!open) return null;

  const submit = (prompt: string) => {
    if (!prompt.trim()) return;
    onAsk(prompt.trim());
    setInput('');
  };

  return createPortal(
    <div onClick={onClose} className="fixed inset-0 z-[1000] bg-black/30 flex justify-end">
      <div onClick={(e) => e.stopPropagation()} className="bg-white border-l border-line shadow-2xl w-full max-w-[440px] h-full flex flex-col">
        <div className="px-5 py-4 bg-gradient-to-br from-[#F5F3FF] to-[#FDFBFF] border-b border-[#E9D8FD] flex items-center gap-2.5">
          <span className="w-8 h-8 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#6B46C1] text-white text-[14px] font-extrabold inline-flex items-center justify-center">
            V
          </span>
          <h4 className="text-[15px] font-extrabold text-vela-deep m-0">Ask Vela about this report</h4>
          <button type="button" onClick={onClose} aria-label="Close" className="text-vela-deep cursor-pointer border-none bg-transparent hover:brightness-110 ml-auto">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          <div className="text-[11.5px] text-ink3 font-bold uppercase tracking-wider mb-2.5">
            Try asking
          </div>
          {VELA_SUGGESTED_PROMPTS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => submit(p)}
              className="w-full text-left p-3 border border-[#E9D8FD] rounded-md mb-2 text-[12.5px] text-vela-deep cursor-pointer hover:bg-vela-fade"
            >
              {p}
            </button>
          ))}
        </div>

        <div className="p-4 border-t border-lineSoft flex gap-1.5">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(input); }}
            placeholder="Ask a question about this report..."
            className="flex-1 bg-white border border-line rounded-md px-3 py-2.5 text-[12.5px] text-ink placeholder:text-ink4 outline-none focus:border-vela-deep"
          />
          <button
            type="button"
            onClick={() => submit(input)}
            disabled={!input.trim()}
            className="bg-vela-deep text-white text-[12px] font-bold px-4 rounded-md border-none cursor-pointer hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Ask
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}